import React, { useEffect } from 'react';
import { t } from '@citrite/translate';
import { Button, layout, LoadingIconPrimary, palette } from '@citrite/web-ui-component';
import styled from '@emotion/styled';
import { sortedResources } from 'App/Screen/util';
import { useServiceContinuityContext } from 'App/ServiceContinuity';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import {
	ActiveLeasesState,
	CategoryFilters,
	FilterType,
	LeaseResourcesList,
	OfflineHeader,
	SyncInfo,
} from './components/index';

const Container = styled.div`
	width: 440px;
	max-width: 440px;
	min-width: 440px;
	height: 660px;
	background: ${palette.white};
	border: 1px solid ${palette.grey200};
	border-radius: ${layout.tinySpace};
	box-shadow: 0px 1px ${layout.mediumLargeSpace} rgba(0, 0, 0, 0.24);
	display: flex;
	flex-direction: column;
	gap: ${layout.largeSpace};
	padding: ${layout.largeSpace} ${layout.mediumLargeSpace} ${layout.mediumLargeSpace};
	overflow: hidden;
	box-sizing: border-box;
	justify-content: center;
`;

const FlexColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${layout.mediumLargeSpace};
`;

const SpinnerDiv = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
`;

const RESYNC_INITIATED_THRESHOLD = 15 * 60 * 1000; // 15 minutes

export const ServiceContinuityManager: React.FC = () => {
	const {
		shieldStatus,
		initializeLeaseSync,
		lastClientResyncTriggerTime,
		initializeLogCollection,
		syncPlatformLeaseInformation,
		isLoading,
	} = useServiceContinuityContext();
	const [affectedResources, setAffectedResources] = React.useState<Resource[]>([]);
	const [areAllLeasesActive, setAreAllLeasesActive] = React.useState<boolean>(false);
	const [isResyncInitiated, setIsResyncInitiated] = React.useState<boolean>(false);

	const [activeFilter, setActiveFilter] = React.useState<FilterType>('all');

	useEffect(() => {
		syncPlatformLeaseInformation();
	}, [syncPlatformLeaseInformation]);

	useEffect(() => {
		let filteredResources: Resource[] = [];

		switch (activeFilter) {
			case 'expired':
				filteredResources = [...shieldStatus.expiredLeases];
				break;
			case 'expiring':
				filteredResources = [...shieldStatus.expiringLeases];
				break;
			default:
				filteredResources = [
					...shieldStatus.expiredLeases,
					...shieldStatus.expiringLeases,
				];
		}

		setAffectedResources(sortedResources(filteredResources));
	}, [shieldStatus?.expiredLeases, shieldStatus?.expiringLeases, activeFilter]);

	useEffect(() => {
		if (!lastClientResyncTriggerTime) {
			setIsResyncInitiated(false);
			return undefined;
		}

		const timeSinceLastResync = Date.now() - lastClientResyncTriggerTime.getTime();
		const isWithinThreshold = timeSinceLastResync < RESYNC_INITIATED_THRESHOLD;

		setIsResyncInitiated(isWithinThreshold);

		if (!isWithinThreshold) {
			return undefined;
		}

		const timeoutId = setTimeout(() => {
			setIsResyncInitiated(false);
		}, RESYNC_INITIATED_THRESHOLD - timeSinceLastResync);

		return () => clearTimeout(timeoutId);
	}, [lastClientResyncTriggerTime]);

	useEffect(() => {
		setAreAllLeasesActive(
			shieldStatus.expiredLeases.length === 0 && shieldStatus.expiringLeases.length === 0
		);
	}, [shieldStatus?.expiredLeases, shieldStatus?.expiringLeases]);

	const onContainerClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
	};

	const handleFilterChange = (filter: FilterType) => {
		setActiveFilter(filter);
	};

	return (
		<Container onClick={onContainerClick}>
			{isLoading ? (
				<SpinnerDiv>
					<LoadingIconPrimary size={48} />
				</SpinnerDiv>
			) : (
				<FlexColumn>
					<OfflineHeader
						title={t('Workspace:service_continuity.offline_availability_title')}
						description={
							!areAllLeasesActive &&
							t('Workspace:service_continuity.offline_availability_description')
						}
					/>
					{areAllLeasesActive ? (
						<ActiveLeasesState
							activeLeasesCount={shieldStatus.activeLeases.length}
							lastSyncTime={shieldStatus.lastLeaseSyncSuccessTime}
						/>
					) : (
						<FlexColumn>
							<CategoryFilters
								expiredCount={shieldStatus.expiredLeases.length}
								expiringSoonCount={shieldStatus.expiringLeases.length}
								activeFilter={activeFilter}
								onFilterChange={handleFilterChange}
							/>

							<SyncInfo
								lastSyncTime={shieldStatus.lastLeaseSyncSuccessTime}
								onResync={initializeLeaseSync}
								isResyncing={isResyncInitiated}
							/>

							<LeaseResourcesList resources={affectedResources} />

							{initializeLogCollection && (
								<div style={{ display: 'flex', gap: '16px' }}>
									<Button.Secondary onClick={initializeLogCollection}>
										{t('Workspace:service_continuity.collect_logs')}
									</Button.Secondary>
								</div>
							)}
						</FlexColumn>
					)}
				</FlexColumn>
			)}
		</Container>
	);
};
